import React, { ReactNode } from "react";
import { View, StyleSheet } from "react-native";
import { colors, layout } from "@/constants";
import Card from "./Card";
import Icon, { IconName } from "./Icon";
import Text from "./Text";

/** Visual variant for the notification card */
type NotificationVariant = "info" | "error";

/** Default icons per variant */
const defaultIcons: Record<NotificationVariant, IconName> = {
  info: "info",
  error: "error"
};

/**
 * Props for the NotificationCard component
 */
interface NotificationCardProps {
  /** Material Symbols icon name — defaults to "error" or "info" based on variant */
  icon?: IconName;
  /** Icon color override — defaults based on variant */
  iconColor?: string;
  /** Visual variant: "info" (light bg) or "error" (dark bg, white text) */
  variant?: NotificationVariant;
  /** Error/notification label (convenience alternative to children) */
  label?: string;
  /** Error/notification message (convenience alternative to children) */
  message?: string;
  /** Message content — takes precedence over label/message when provided */
  children?: ReactNode;
}

/**
 * Notification card for status messages with an icon.
 * Used for waitlist confirmation, email verification, password reset, errors, etc.
 */
export default function NotificationCard({
  icon,
  iconColor,
  variant = "info",
  label,
  message,
  children
}: NotificationCardProps) {
  const isError = variant === "error";
  const resolvedIcon = icon ?? defaultIcons[variant];
  const resolvedIconColor =
    iconColor ?? (isError ? colors.error : colors.primary);

  const content =
    children ??
    (label && message ? (
      <Text
        variant="regular"
        size="small"
        style={isError ? styles.errorText : styles.infoText}
        numberOfLines={3}
        ellipsizeMode="tail"
      >
        <Text
          variant="semiBold"
          style={{
            color: isError ? colors.inverseOnSurface : colors.onSurface
          }}
        >
          {label}:
        </Text>{" "}
        {message}
      </Text>
    ) : null);

  return (
    <Card style={isError ? styles.errorCard : styles.infoCard}>
      <View style={styles.row}>
        <Icon
          name={resolvedIcon}
          size="medium"
          style={[styles.icon, { color: resolvedIconColor }]}
        />
        {content}
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  infoCard: {
    backgroundColor: colors.successContainer
  },
  errorCard: {
    backgroundColor: colors.inverseSurface
  },
  row: {
    flexDirection: "row",
    alignItems: "center"
  },
  icon: {
    marginRight: layout.space.sm
  },
  errorText: {
    color: colors.inverseOnSurface,
    flex: 1
  },
  infoText: {
    color: colors.onSurface,
    flex: 1
  }
});
